import { Route, getRouteUrl } from '@bymason-private/route-provider';
import { LoginView } from '@bymason-private/auth-provider';
import AccountView from '../views/account';
import ArtifactsView from '../views/artifacts';
import ConnectivityView from '../views/connectivity';
import DeviceView from '../views/device';
import DevicesView from '../views/devices';
import GroupView from '../views/group';
import GroupsView from '../views/groups';
import ProjectView from '../views/project';
import ProjectsView from '../views/projects';
import SignUpView from '../views/signup';
import ValidateMessage from '../views/not-validated';
import UsersView from '../views/users';
import ApiKeysView from '../views/api-keys';
import ProvisionView from '../views/provision';
import NotifcationsView from '../views/notifications';
import DashboardView from '../views/dashboard';
import GlobalStatusAlert from '../common/global-status-alert';

/**
 * This is a type helper that allows the use of a Record<string, Route> while
 * still maintaining intellisense on routes object in the IDE.
 */
function asRoutes<T extends { [key: string]: Route }>(arg: T): T {
  return arg;
}

/**
 * This is the single source of truth for all Controller routes.
 */
export const routes = asRoutes({
  Account: {
    path: '/settings/account',
    viewComponent: <AccountView />,
  },
  ApiKeys: {
    path: '/settings/apikeys',
    viewComponent: <ApiKeysView />,
  },
  User: {
    path: '/settings/users',
    viewComponent: <UsersView />,
  },
  Notifications: {
    path: '/settings/notifications',
    viewComponent: <NotifcationsView />,
  },
  Artifacts: {
    path: '/artifacts',
    viewComponent: <ArtifactsView />,
  },
  Connectivity: {
    path: '/connectivity',
    viewComponent: <ConnectivityView />,
  },
  ConnectivitySims: {
    path: '/connectivity/:iccid',
    viewComponent: <ConnectivityView />,
  },
  Dashboard: {
    path: '/dashboard',
    viewComponent: <DashboardView />,
  },
  Provision: {
    path: '/devices/provision',
    viewComponent: <ProvisionView />,
  },
  Devices: {
    path: '/devices',
    viewComponent: <DevicesView />,
  },
  Device: {
    path: '/devices/:deviceId/:subView',
    viewComponent: <DeviceView />,
  },

  Group: {
    path: '/groups/:name',
    viewComponent: <GroupView />,
  },
  Groups: {
    path: '/groups',
    viewComponent: <GroupsView />,
  },

  Login: {
    // IMPORTANT: do not change this path as it
    // must match configuration set in Auth0
    path: '/login',
    public: true,
    viewComponent: <LoginView statusMessage={<GlobalStatusAlert sx={{ maxWidth: 700 }} />} />,
  },
  ProjectBuildStepper: {
    path: '/projects/:projectId/build/:stepId',
    viewComponent: <ProjectView action='build' />,
  },
  Project: {
    path: '/projects/:projectId',
    viewComponent: <ProjectView />,
  },
  Projects: {
    path: '/projects',
    viewComponent: <ProjectsView />,
  },

  SignUp: {
    path: '/signup',
    public: true,
    viewComponent: <SignUpView />,
  },
  Unvalidated: {
    path: '/unvalidated',
    public: true,
    viewComponent: <ValidateMessage />,
  },
  Logout: {
    path: '/logout',
  },
});

/**
 * The route that will be rendered when no path is present (i.e. root)
 */
export const defaultRoute = routes.Dashboard;

/**
 * The login route that the router will render when user is not authenticated
 */
export const loginRoute = routes.Login;

/**
 * The logout route that the route will detect and trigger logout workflow.
 */
export const logoutRoute = routes.Logout;

/**
 * The URL to which an unvalidated account is redirected
 */
export const unvalidatedRoute = routes.Unvalidated;

/**
 * The login URL that is required by the AuthProvider
 */
export const loginUrl = getRouteUrl(routes.Login.path);

/**
 * The post-login callback URL that is required by the AuthProvider
 */
export const loginCallbackUrl = getRouteUrl('callback');
